/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  GridCallbackDetails,
  GridRowParams,
  MuiEvent,
} from '@spotgamma/x-data-grid-premium';
import {
  defaultColumnScannerVisibility,
  scannerFields,
  EQUITYHUB_UPSELL,
} from '../../config';
import useAuth from '../../hooks/auth/useAuth';
import usePrices from '../../hooks/equityhub/usePrices';
import {
  totalOISynthesizedEquitiesState,
  currentToastState,
  pricesState,
  equitiesFetchLoadingState,
  watchlistsState,
  selectedEquitySymbolState,
  userIsLoggedInState,
} from '../../states';
import {
  ProductType,
  LegacyEquity,
  SynthOIEquity,
  CoreEquity,
} from '../../types';
import {
  hasKeyEquityFields,
  getColumnsForScanner,
  getScannerData,
  sanitizeFilterModel,
} from '../../util';
import {
  useEquityGridColumns,
  EquityTableType,
} from '../equityhub/EquityhubTable/useEquityScannerGridColumns';
import { InfoButton, UpsellModal, WatchlistMultiSelect } from '../shared';
import { EquityScannerGrid } from './EquityScannerGrid';
import ScannersSection from './ScannersSection';
import { compassActiveSymbolsState } from 'states/compass';
import { ChartSizingButton } from 'components/shared/ChartSizingButton';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExitSharp';
import FullscreenIcon from '@mui/icons-material/FullscreenSharp';

import { useScannerUrlState } from 'hooks/scanners/useScannerUrlState';
import { useScannerGridState } from 'hooks/scanners/useScannerGridState';
import { usePersistedState } from 'hooks/usePersistedState';

interface EquityStockScannerProps {
  stockDetailsFullscreen: boolean;
  setStockDetailsFullscreen: (fullscreen: boolean) => void;
}

export const EquityStockScanner = ({
  stockDetailsFullscreen,
  setStockDetailsFullscreen,
}: EquityStockScannerProps) => {
  const theme = useTheme();
  const equities = useRecoilValue(totalOISynthesizedEquitiesState);
  const setToast = useSetRecoilState(currentToastState);
  const setPrices = useSetRecoilState(pricesState);
  const [pricesLoading, setPricesLoading] = useState<boolean>(false);
  const equitiesLoading = useRecoilValue(equitiesFetchLoadingState);
  const watchlists = useRecoilValue(watchlistsState);
  const [dpiRange, setDPIRange] = useState<number[] | undefined>(undefined);
  const { hasAccessToProduct } = useAuth();
  const hasScannersAccess = hasAccessToProduct(ProductType.SCANNERS);
  const [currentSym, setCurrentSym] = useRecoilState(selectedEquitySymbolState);
  const [upsellOpen, setUpsellOpen] = useState<boolean>(false);
  const setCompassActiveSymbols = useSetRecoilState(compassActiveSymbolsState);
  const isLoggedIn = useRecoilValue(userIsLoggedInState);
  const { getPrices } = usePrices();

  // The /scanners route always shows Total OI data. We never read the global
  // model toggle here; instead we treat the model as Total OI (Legacy) so the
  // column set and row data are consistent and independent of ehModelTypeState.
  const isSynthOI = false;

  // Scanner selection via URL
  const { activeScanner, setActiveScanner } = useScannerUrlState();

  // Grid internals via useState
  const gridState = useScannerGridState();

  const { columns: gridCols, columnGroups: gridColGroups } =
    useEquityGridColumns(
      theme,
      dpiRange,
      EquityTableType.StockScreener,
      isSynthOI,
      !hasScannersAccess
        ? [...equities.values()]
            .filter((e) => !hasKeyEquityFields(e))
            .map((e) => e.sym)
        : undefined,
    );

  useEffect(() => {
    const dpiArray = [...equities.values()].map((d: CoreEquity) => d.dpi);
    setDPIRange([Math.min(...dpiArray), Math.max(...dpiArray)]);
  }, [equities]);

  useEffect(() => {
    async function fetchVisiblePriceData() {
      try {
        setPricesLoading(true);
        // Just fetch all prices. It's a small payload
        const newPrices: Map<string, number> = await getPrices([]);
        setPrices((prices) => new Map([...prices, ...newPrices]));
      } catch (err) {
        console.error(err);
        setToast({
          message:
            'Something went wrong while fetching equities price data. Try again or contact us if the issue persists.',
          type: 'error',
          duration: 10_000,
        });
      } finally {
        setPricesLoading(false);
      }
    }
    fetchVisiblePriceData();
  }, [getPrices, setPrices]);

  useEffect(() => {
    if (gridState.useDefaultColumns) {
      gridState.setColumnVisibilityModel(
        activeScanner
          ? getColumnsForScanner(activeScanner)
          : defaultColumnScannerVisibility,
      );
    }
  }, [gridState.useDefaultColumns, activeScanner]);

  const scannerData = useMemo(
    () =>
      getScannerData({
        equities,
        watchlists,
        activeWatchlistIds: gridState.activeWatchlistIds,
        activeScanner,
        hasAccess: hasScannersAccess,
      }),
    [
      activeScanner,
      gridState.activeWatchlistIds,
      hasScannersAccess,
      watchlists,
      equities,
    ],
  );

  // Persist all scanner state to localStorage
  const persistedState = useMemo(
    () => ({
      scanner: activeScanner,
      ...gridState,
    }),
    [activeScanner, gridState],
  );

  const restoreState = useCallback(
    (saved: typeof persistedState) => {
      if (saved.scanner !== undefined) {
        setActiveScanner(saved.scanner);
      }
      if (saved.activeWatchlistIds) {
        gridState.setActiveWatchlistIds(saved.activeWatchlistIds);
      }
      if (saved.sortModel) {
        gridState.setSortModel(saved.sortModel);
      }
      if (saved.columnVisibilityModel) {
        gridState.setColumnVisibilityModel(saved.columnVisibilityModel);
      }
      if (saved.pinnedColumns) {
        gridState.setPinnedColumns(saved.pinnedColumns);
      }
      if (saved.filterModel !== undefined) {
        gridState.setFilterModel(
          sanitizeFilterModel(saved.filterModel, gridCols),
        );
      }
      if (saved.columnOrder) {
        gridState.setColumnOrder(saved.columnOrder);
      }
      if (saved.useDefaultColumns !== undefined) {
        gridState.setUseDefaultColumns(saved.useDefaultColumns);
      }
    },
    [setActiveScanner, gridState, gridCols],
  );

  usePersistedState('scanner_grid_state', persistedState, restoreState, [
    'scanner',
  ]);

  const setSelectedRow = (row: LegacyEquity | SynthOIEquity) => {
    const newSym = row.sym;
    setCurrentSym(newSym);
    setCompassActiveSymbols((prev) => Array.from(new Set([...prev, newSym])));
  };

  const isRowDisabled = (row: LegacyEquity | SynthOIEquity) => {
    return !hasScannersAccess && !row.live;
  };

  const onRowClick = (
    params: GridRowParams,
    _event: MuiEvent,
    _details: GridCallbackDetails,
  ) => {
    const row = params.row as LegacyEquity | SynthOIEquity;
    if (isRowDisabled(row)) {
      setUpsellOpen(true);
    } else {
      setSelectedRow(row);
    }
  };

  return (
    <Box
      sx={{
        width: 'fit-content',
        maxWidth: '100%',
        overflow: 'hidden',
        flex: 1,
      }}
    >
      <EquityScannerGrid
        currentSym={currentSym}
        columns={gridCols}
        data={scannerData}
        columnGroups={gridColGroups}
        onRowClick={onRowClick}
        loading={equitiesLoading || pricesLoading}
        activeScanner={activeScanner}
        activeWatchlistIds={gridState.activeWatchlistIds}
        useDefaultColumns={gridState.useDefaultColumns}
        setUseDefaultColumns={gridState.setUseDefaultColumns}
        columnVisibilityModel={gridState.columnVisibilityModel}
        setColumnVisibilityModel={gridState.setColumnVisibilityModel}
        pinnedColumns={gridState.pinnedColumns}
        setPinnedColumns={gridState.setPinnedColumns}
        sortModel={gridState.sortModel}
        setSortModel={gridState.setSortModel}
        filterModel={gridState.filterModel}
        setFilterModel={gridState.setFilterModel}
        columnOrder={gridState.columnOrder}
        setColumnOrder={gridState.setColumnOrder}
        defaultColumnOrder={scannerFields}
        customSettings={
          <Box>
            <InfoButton key="scanners" articleKey="scanners" />
            {isLoggedIn && (
              <WatchlistMultiSelect
                activeWatchlistIds={gridState.activeWatchlistIds}
                setActiveWatchlistIds={gridState.setActiveWatchlistIds}
              />
            )}
            <ChartSizingButton
              onClick={() => setStockDetailsFullscreen(!stockDetailsFullscreen)}
              icon={
                stockDetailsFullscreen ? (
                  <FullscreenExitIcon fontSize="small" color="primary" />
                ) : (
                  <FullscreenIcon fontSize="small" color="primary" />
                )
              }
              title="Fullscreen"
              id="stock-details-fullscreen-button"
            />
          </Box>
        }
        customScannerSettings={
          <ScannersSection
            activeScanner={activeScanner}
            setActiveScanner={setActiveScanner}
            isSynthOI={false}
          />
        }
        showScannerInfo
      />
      <UpsellModal
        open={upsellOpen}
        setOpen={setUpsellOpen}
        title={EQUITYHUB_UPSELL.title}
        subtitle={EQUITYHUB_UPSELL.subtitle}
        items={EQUITYHUB_UPSELL.items}
      />
    </Box>
  );
};
